import { useRef, useCallback, useEffect } from 'react';

export interface DynamicPollingOptions<T> {
  /**
   * Array de intervalos em segundos para o pooling
   * Exemplo: [5, 10, 15] = aguarda 5s, depois 10s, depois 15s
   */
  intervals: number[];

  /**
   * Função que será executada a cada iteração do pooling
   * Deve retornar uma Promise com o resultado
   */
  pollFunction: () => Promise<T>;

  /**
   * Função que determina se deve continuar o pooling baseado no resultado
   * Retorna true para continuar, false para parar
   */
  shouldContinue: (result: T) => boolean;

  /**
   * Função chamada quando há sucesso (shouldContinue retorna false)
   */
  onSuccess: (result: T) => void;

  /**
   * Função chamada quando há erro
   */
  onError: (error: unknown) => void;

  /**
   * Função chamada quando o pooling termina sem sucesso (esgota todos os intervalos)
   */
  onTimeout: () => void;

  /**
   * Função chamada a cada intervalo completo do pooling
   */
  onInterval?: (interval: number) => void;
}

export interface DynamicPollingReturn {
  /**
   * Inicia o pooling manualmente
   */
  startPolling: () => void;

  /**
   * Para o pooling atual
   */
  stopPolling: () => void;

  /**
   * Indica se o pooling está em execução
   */
  isPolling: boolean;
}

/**
 * Hook customizado para pooling dinâmico com intervalos configuráveis
 *
 * @example
 * ```typescript
 * const { startPolling, stopPolling, isPolling } = useDynamicPolling({
 *   intervals: [5, 10, 15], // 5s, 10s, 15s
 *   pollFunction: async () => {
 *     const response = await api.checkStatus();
 *     return response.data;
 *   },
 *   shouldContinue: (result) => !result.isComplete,
 *   onSuccess: (result) => navigate('/success'),
 *   onError: (error) => navigate('/error'),
 *   onTimeout: () => navigate('/timeout')
 * });
 * ```
 */
export const useDynamicPolling = <T>({
  intervals,
  pollFunction,
  shouldContinue,
  onSuccess,
  onError,
  onTimeout,
  onInterval,
}: DynamicPollingOptions<T>): DynamicPollingReturn => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const isExecutingRef = useRef<boolean>(false);
  const pendingResultRef = useRef<T | null>(null);
  const pendingSuccessRef = useRef<boolean>(false);

  const stopPolling = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    isExecutingRef.current = false;
  }, []);

  // Monitora visibilidade da aba para executar callbacks pendentes
  usePageVisibility(onSuccess, pendingResultRef, pendingSuccessRef);

  const startPolling = useCallback(async () => {
    // Previne execução simultânea
    if (isExecutingRef.current) {
      return;
    }

    // Valida se há intervalos configurados
    if (!intervals || intervals.length === 0) {
      onError(new Error('Intervalos não configurados'));
      return;
    }

    isExecutingRef.current = true;

    try {
      // Cancela qualquer pooling anterior se existir
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Cria um novo AbortController para este pooling
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      for (let i = 0; i < intervals.length; i++) {
        // Verifica se o pooling foi cancelado antes de cada iteração
        if (abortController.signal.aborted) {
          return;
        }

        const intervalTime = intervals[i];

        // Aguarda o tempo do intervalo (se maior que 0)
        if (intervalTime > 0) {
          await new Promise<void>((resolve) => {
            const timeoutId = setTimeout(resolve, intervalTime * 1000);

            // Se cancelado, limpa o timeout
            const onAbort = () => {
              clearTimeout(timeoutId);
              resolve();
            };

            abortController.signal.addEventListener('abort', onAbort, {
              once: true,
            });
          });
        }

        // Verifica novamente se foi cancelado após o timeout
        if (abortController.signal.aborted) {
          return;
        }

        try {
          // Executa a função de pooling
          const result = await pollFunction();

          // Verifica se foi cancelado durante a execução
          if (abortController.signal.aborted) {
            return;
          }

          // Verifica se deve continuar o pooling
          if (!shouldContinue(result)) {
            // Se a aba está em background, armazena o resultado e aguarda visibilidade
            if (document.hidden) {
              pendingResultRef.current = result;
              pendingSuccessRef.current = true;
              return;
            }
            onSuccess(result);
            return;
          }

          // Executa uma ação a cada intervalo completo do pooling sem sucesso ou erro
          onInterval?.(i);
        } catch (error) {
          // Chama callback de erro e para o pooling
          onError(error);
          return;
        }

        // Verifica uma última vez antes da próxima iteração
        if (abortController.signal.aborted) {
          return;
        }
      }

      // Se chegou até aqui, esgotou todos os intervalos sem sucesso
      if (!abortController.signal.aborted) {
        onTimeout();
      }
    } catch (error) {
      onError(error);
    } finally {
      // Reset flag para permitir nova execução
      isExecutingRef.current = false;
    }
  }, [
    intervals,
    pollFunction,
    shouldContinue,
    onSuccess,
    onError,
    onTimeout,
    onInterval,
  ]);

  return {
    startPolling,
    stopPolling,
    isPolling: isExecutingRef.current,
  };
};

// Hook adicional para gerenciar visibilidade de aba
function usePageVisibility<T>(
  onVisible: (result: T) => void,
  pendingResultRef: React.MutableRefObject<T | null>,
  pendingSuccessRef: React.MutableRefObject<boolean>
) {
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        !document.hidden &&
        pendingSuccessRef.current &&
        pendingResultRef.current
      ) {
        onVisible(pendingResultRef.current);
        pendingResultRef.current = null;
        pendingSuccessRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onVisible]);
}
